import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useSnackbar } from 'notistack';
import { getApiKeysBySession } from '../api/magicLinkApi';
import EditApiKeyForm from './EditApiKeyForm';
import DynamicSnackbarMessage from './DynamicSnackbarMessage';
import { useSession } from '../contexts/SessionContext';

function KeyDetailPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { enqueueSnackbar } = useSnackbar();
  const { setAuthenticated } = useSession();
  const [isValid, setIsValid] = useState<boolean | null>(null);
  const [apiKeyCount, setApiKeyCount] = useState<number>(0);
  const [isActive, setIsActive] = useState<boolean>(false);
  const [apiKey] = useState(() =>
    localStorage.getItem('last_selected_api_key')
  );

  useEffect(() => {
    // Show activation success notification
    if (searchParams.get('activated') === '1') {
      const stored = sessionStorage.getItem('activation_result');
      if (stored) {
        sessionStorage.removeItem('activation_result');
        enqueueSnackbar(
          <DynamicSnackbarMessage textKey="ACTIVATION_SNACKBAR" />,
          {
            variant: 'info',
            persist: true,
          }
        );
      }
    }

    if (!apiKey) {
      navigate('/keys', { replace: true });
      return;
    }

    // Validate session and that the key belongs to the user
    const validate = async () => {
      try {
        const keys = await getApiKeysBySession();
        const matched = keys.find((k) => k.apiKey === apiKey);
        if (matched) {
          setApiKeyCount(keys.length);
          setIsActive(matched.isActive);
          setIsValid(true);
          enqueueSnackbar(
            <DynamicSnackbarMessage textKey="SESSION_TIMEOUT_WARNING" />,
            {
              variant: 'info',
              persist: true,
              key: 'session-timeout-warning',
              preventDuplicate: true,
            }
          );
        } else {
          localStorage.removeItem('last_selected_api_key');
          navigate('/keys', { replace: true });
        }
      } catch {
        setAuthenticated(false);
        navigate('/', { replace: true });
      }
    };

    validate();
  }, [apiKey]);

  const handleClose = () => {
    localStorage.removeItem('last_selected_api_key');
    navigate('/keys');
  };

  if (isValid === null) {
    return (
      <Box sx={{ textAlign: 'center', py: 6 }}>
        <CircularProgress size={48} />
      </Box>
    );
  }

  return (
    <EditApiKeyForm
      apiKey={apiKey!}
      onClose={handleClose}
      apiKeyCount={apiKeyCount}
      isActive={isActive}
    />
  );
}

export default KeyDetailPage;
